<script lang="ts">
	import * as m from '$lib/paraglide/messages.js'
	import { tick } from 'svelte'
	import { goto } from '$app/navigation'
	import { localizeHref } from '$lib/paraglide/runtime'

	interface Props {
		// Localizable text
		placeholderText?: m.LocalizedString
		buttonText?: m.LocalizedString
		headingText?: m.LocalizedString
		descriptionText?: m.LocalizedString
		// State variable for email binding (for external use)
		email?: string
		/** When set, submit routes to this signup path with the email prefilled, instead of Substack. */
		handoffHref?: string
	}

	let {
		placeholderText = m.newsletter_email_placeholder(),
		buttonText = m.newsletter_button(),
		headingText = m.newsletter_heading(),
		descriptionText = m.newsletter_description(),
		email = $bindable(''),
		handoffHref = undefined
	}: Props = $props()

	// State for showing success message
	let showSuccess = $state(false)
	let formElement: HTMLFormElement

	const handleSubmit = async (e: Event) => {
		e.preventDefault()

		// Newsletter entry that feeds the CRM: hand off to the signup flow with the
		// email prefilled, rather than subscribing to Substack directly.
		if (handoffHref) {
			const dest = `${localizeHref(handoffHref)}?subscribe-email=${encodeURIComponent(email)}`
			email = ''
			void goto(dest)
			return
		}

		// Show success message immediately
		showSuccess = true

		// Clear the email field
		email = ''

		// Wait for Svelte to render the success message
		await tick()

		// Now submit the form to open new tab
		formElement.submit()
	}
</script>

<div class="newsletter-signup">
	<div class="newsletter-content">
		<h3 class="toc-exclude">{headingText}</h3>
		<p>{descriptionText}</p>

		<!-- Direct POST to Substack API in new tab, unless handing off to the signup
		     flow — then the native target mirrors the hydrated goto(), so submitting
		     before hydration (or without JS) still lands on the flow rather than
		     subscribing straight to Substack. -->
		<form
			bind:this={formElement}
			action={handoffHref ? localizeHref(handoffHref) : 'https://pauseai.substack.com/api/v1/free'}
			method={handoffHref ? 'GET' : 'POST'}
			target={handoffHref ? undefined : '_blank'}
			onsubmit={handleSubmit}
		>
			<div class="input-group">
				<input
					type="email"
					name={handoffHref ? 'subscribe-email' : 'email'}
					bind:value={email}
					placeholder={placeholderText}
					aria-label={placeholderText}
					required
					enterkeyhint="done"
				/>
				{#if !handoffHref}
					<input type="hidden" name="source" value="pauseai_website" />
				{/if}
				<button type="submit">{buttonText}</button>
			</div>
		</form>

		{#if showSuccess}
			<div class="message success">
				{m.newsletter_success()}
			</div>
		{/if}
	</div>
</div>

<style>
	.newsletter-signup {
		background-color: var(--bg-subtle);
		border-radius: 8px;
		padding: 1.5rem;
		margin: 2rem 0;
		width: 100%;
		box-sizing: border-box;
	}

	.newsletter-content {
		max-width: 500px;
		margin: 0 auto;
	}

	h3 {
		margin-top: 0;
		margin-bottom: 0.5rem;
		font-family: var(--font-heading);
	}

	p:last-child {
		margin-bottom: 0;
	}

	.input-group {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	input {
		flex: 1;
		padding: 0.75rem 1rem;
		border: 1px solid var(--border);
		border-radius: 4px;
		font-size: 1rem;
	}

	button {
		padding: 0.75rem 1.5rem;
		border: none;
		border-radius: 4px;
		font-weight: bold;
		cursor: pointer;
		transition: opacity 0.2s;
		font-size: 1rem;
		background-color: var(--brand);
		color: var(--bg);
	}

	button:hover {
		opacity: 0.9;
	}

	button:disabled {
		opacity: 0.7;
		cursor: not-allowed;
	}

	.message {
		margin-top: 10px;
		padding: 8px;
		border-radius: 4px;
		font-size: 0.9rem;
	}

	.success {
		background-color: var(--status-success-bg-alt);
		color: var(--status-success-text-alt);
	}

	@media (max-width: 600px) {
		.input-group {
			flex-direction: column;
		}

		input,
		button {
			box-sizing: border-box;
			width: 100%;
		}
	}
</style>
